import { useMemo, useState } from "react";

import { RoleGate } from "@/components/RoleGate";
import {
  useCaixas,
  useLocais,
  useReporCaixa,
  useReposicaoCaixa,
} from "@/hooks/useApi";
import { useUser } from "@/hooks/useUser";
import type { Caixa } from "@/types/api";

const TIPO_LABEL: Record<string, string> = {
  caixa_anestesia: "Caixa de Anestesia",
  caixa_psicotropicos: "Caixa de Psicotrópicos (C.Cir.)",
  carrinho_parada: "Carrinho de Parada",
};

export function CaixasPage() {
  return (
    <RoleGate allowed={["admin", "auditor", "farmaceutico"]}>
      <Inner />
    </RoleGate>
  );
}

function Inner() {
  const { data: caixas, isLoading } = useCaixas();
  const [aberta, setAberta] = useState<string | null>(null);
  const [imprimir, setImprimir] = useState<string | null>(null);

  const grupos = useMemo(() => {
    const g: Record<string, Caixa[]> = {};
    for (const c of caixas ?? []) (g[c.tipo] ??= []).push(c);
    return g;
  }, [caixas]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-semibold">Caixas / Kits</h2>
        <p className="text-sm text-ink-muted">
          Reposição por par-level: o sistema compara a composição-alvo com o que há na
          caixa e repõe a diferença (FEFO da farmácia). Só farmácia repõe.
        </p>
      </div>

      {isLoading ? <p className="text-ink-muted">Carregando…</p> : null}
      {Object.entries(grupos).map(([tipo, lista]) => (
        <div key={tipo} className="space-y-2">
          <h3 className="font-display text-base font-semibold">{TIPO_LABEL[tipo] ?? tipo}</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {lista.map((c) => (
              <div key={c.id} className="card p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <b>{c.descricao}</b>
                    <p className="text-xs text-ink-muted">{c.unidade}</p>
                  </div>
                  <span className="badge bg-prata-100 text-prata-700">{c.total_atual} un</span>
                </div>
                <p className="mt-1 text-xs text-ink-muted">
                  {c.itens_alvo > 0 ? `${c.itens_alvo} itens na composição` : "⚠️ composição não cadastrada"}
                </p>
                <div className="mt-2 flex gap-3 text-sm">
                  <button className="text-anarosa hover:underline" onClick={() => setAberta(c.id)}>
                    Repor
                  </button>
                  <button className="text-anarosa hover:underline" onClick={() => setImprimir(c.id)}>
                    Imprimir folha
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {aberta ? <ReporModal localId={aberta} onClose={() => setAberta(null)} /> : null}
      {imprimir ? <ImprimirModal localId={imprimir} onClose={() => setImprimir(null)} /> : null}
    </div>
  );
}

function ReporModal({ localId, onClose }: { localId: string; onClose: () => void }) {
  const { data, isLoading } = useReposicaoCaixa(localId);
  const { data: locais } = useLocais();
  const { data: user } = useUser();
  const repor = useReporCaixa();
  const farmacias = (locais ?? []).filter((l) => l.tipo === "farmacia");
  const [origem, setOrigem] = useState("");
  const [qtds, setQtds] = useState<Record<string, string>>({});

  const podeRepor = user && ["admin", "farmaceutico"].includes(user.papel);

  function valor(medId: string, sugerido: number): string {
    return qtds[medId] ?? String(sugerido);
  }

  async function submit() {
    if (!data || !origem) return;
    const itens = data.itens
      .map((it) => ({ medicamento_id: it.medicamento_id, quantidade: Number(valor(it.medicamento_id, it.qtd_repor)) }))
      .filter((i) => i.quantidade > 0);
    if (!itens.length) return;
    try {
      await repor.mutateAsync({ localId, origem_local_id: origem, itens });
      setTimeout(onClose, 1800);
    } catch { /* erro via repor.error */ }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="card max-h-[88vh] w-full max-w-2xl overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <h3 className="font-display text-xl font-semibold">Repor — {data?.descricao}</h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink">✕</button>
        </div>

        {isLoading || !data ? (
          <p className="text-ink-muted">Carregando…</p>
        ) : data.composicao_vazia ? (
          <p className="text-amber-700 text-sm">
            ⚠️ Esta caixa ainda não tem composição-alvo cadastrada. Cadastre o de-para
            (com a Romilda) em Admin → Composição de Kits antes de repor.
          </p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-muted">Farmácia de origem</label>
              <select className="input mt-1" value={origem} onChange={(e) => setOrigem(e.target.value)}>
                <option value="">— escolha —</option>
                {farmacias.map((f) => <option key={f.id} value={f.id}>{f.descricao}</option>)}
              </select>
            </div>

            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="py-1">Medicamento</th>
                  <th className="py-1 text-right">Alvo</th>
                  <th className="py-1 text-right">Atual</th>
                  <th className="py-1 text-right">Repor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.itens.map((it) => (
                  <tr key={it.medicamento_id}>
                    <td className="py-1.5">
                      <code className="text-xs">{it.medicamento_codigo}</code> {it.medicamento_descricao}
                      {it.portaria_344 ? <span className="badge bg-amber-100 text-amber-800 ml-1">344</span> : null}
                    </td>
                    <td className="py-1.5 text-right">{it.qtd_alvo}</td>
                    <td className="py-1.5 text-right">{it.qtd_atual}</td>
                    <td className="py-1.5 text-right">
                      <input
                        className="input !py-1 w-16 text-right"
                        type="number" min={0}
                        value={valor(it.medicamento_id, it.qtd_repor)}
                        onChange={(e) => setQtds((q) => ({ ...q, [it.medicamento_id]: e.target.value }))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {podeRepor ? (
              <div className="flex items-center gap-3">
                <button className="btn-primary" disabled={!origem || repor.isPending || repor.isSuccess} onClick={submit}>
                  {repor.isPending ? "Repondo…" : "Confirmar reposição"}
                </button>
                {repor.error ? <span className="text-anarosa text-sm">{(repor.error as Error).message}</span> : null}
                {repor.isSuccess ? <span className="text-green-700 text-sm">✅ Caixa reposta.</span> : null}
              </div>
            ) : (
              <p className="text-xs text-ink-muted">Apenas farmácia/admin podem repor.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ImprimirModal({ localId, onClose }: { localId: string; onClose: () => void }) {
  const { data, isLoading } = useReposicaoCaixa(localId);
  function imprimir() { window.print(); }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 print:bg-white print:p-0" onClick={onClose}>
      <div className="card w-full max-w-lg p-6 print:max-w-none print:shadow-none" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between print:hidden">
          <h3 className="font-display text-xl font-semibold">Folha da caixa</h3>
          <div className="flex gap-3">
            <button className="btn-primary !py-1 text-sm" onClick={imprimir}>Imprimir</button>
            <button onClick={onClose} className="text-ink-muted hover:text-ink">✕</button>
          </div>
        </div>
        {isLoading || !data ? <p className="text-ink-muted">Carregando…</p> : (
          <div>
            <h4 className="font-display text-lg font-semibold">{data.descricao}</h4>
            <p className="text-xs text-ink-muted mb-3">Composição atual — gerado pelo Velora Med-Supply</p>
            <table className="w-full text-sm">
              <thead className="text-left border-b border-ink">
                <tr><th className="py-1">Medicamento</th><th className="py-1 text-right">Alvo</th><th className="py-1 text-right">Atual</th></tr>
              </thead>
              <tbody>
                {data.itens.map((it) => (
                  <tr key={it.medicamento_id} className="border-b border-gray-100">
                    <td className="py-1">{it.medicamento_descricao}</td>
                    <td className="py-1 text-right">{it.qtd_alvo}</td>
                    <td className="py-1 text-right">{it.qtd_atual}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
